import { Link } from "react-router-dom";
import { useEffect, useState, useCallback, useRef } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import localeText from "../../../../../locale";
import useDashboardData from "../../../hooks/useDashboardData";
import useCurrency from "../../../../../hooks/useCurrency";
import GetInfoService from "../../../../../service/GetInfoService";
import GetUserInfoService from "../../../../../service/GetUserInfoService";
import ExtracoinService from "../../../../../service/ExtracoinService";
import { useToastContext } from "../../../../../contexts/ToastContext/ToastContext";
import Loading from "../../../../../components/Loading/Loading";
import { formatPrice } from "../../../../../utilities/formatPrice";
import ExtracreditTransferStep1Recipient from "./ExtracreditTransferStep1Recipient";
import ExtracreditTransferStep2Values from "./ExtracreditTransferStep2Values";
import ExtracreditTransferStep3Summary from "./ExtracreditTransferStep3Summary";
import SecurityValidationModal from "../components/SecurityValidationModal/SecurityValidationModal";

const getInfoService = new GetInfoService();
const getUserInfoService = new GetUserInfoService();
const extracoinService = new ExtracoinService();

const ExtracreditTransferPage = ({ userInfo, userTypeInfo, currency: currencyCode }) => {
  const { data, isLoading, refreshData } = useDashboardData();
  const { currencySymbol } = useCurrency(data?.walletInfo?.currency || currencyCode);
  const { showErrorToast, showSuccessToast } = useToastContext();

  const [step, setStep] = useState(1);
  const [recipient, setRecipient] = useState(null);
  const [recipientSearchLoading, setRecipientSearchLoading] = useState(false);
  const [recipientSearchError, setRecipientSearchError] = useState(false);
  const [amount, setAmount] = useState(0);
  const [transferIntentId, setTransferIntentId] = useState(null);
  const [fee, setFee] = useState(null);
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [validatingCode, setValidatingCode] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [securityModalVisible, setSecurityModalVisible] = useState(false);
  const [success, setSuccess] = useState(false);

  const balance = data?.extracoinInfo?.balance ?? 0;
  const pct =
    typeof data?.extracoinPercentage === "number"
      ? data.extracoinPercentage
      : data?.extracoinPercentage?.percent ?? data?.extracoinPercentage ?? 0;
  const usableBalance = balance * (Number(pct) / 100);
  const username = localStorage.getItem("__extraconomy_username");
  const token = localStorage.getItem("__extraconomy_token");

  const searchRecipient = useCallback(async (searchUsername) => {
    setRecipientSearchError(false);
    setRecipientSearchLoading(true);
    try {
      const res = await getInfoService.getFindUsername(searchUsername);
      if (res.messageCode === 200001) {
        const user = res.result?.user ?? res.result;
        setRecipient(
          user && (user.username || user)
            ? { ...(typeof user === "object" ? user : {}), username: user.username ?? searchUsername }
            : { username: searchUsername }
        );
      } else {
        setRecipientSearchError(true);
        setRecipient(null);
      }
    } catch {
      setRecipientSearchError(true);
      setRecipient(null);
    } finally {
      setRecipientSearchLoading(false);
    }
  }, []);

  const fetchTransferIntent = useCallback(
    async (amt) => {
      const numAmount = Number(amt);
      if (!numAmount || !recipient?.username) return;
      setLoadingIntent(true);
      setFee(null);
      setTransferIntentId(null);
      try {
        const res = await extracoinService.postTransferIntent(
          token,
          numAmount,
          username,
          recipient.username
        );
        if (res.messageCode === 200000 || res.messageCode === 200001) {
          const result = res.result;
          setTransferIntentId(result?.id ?? result?.idtransferintent);
          setFee(result?.taxFrom ?? result?.tax ?? 0);
        } else {
          const msg = localeText("data")?.find((m) => m.code === res.messageCode)?.message;
          showErrorToast({ detail: msg || localeText("error") });
        }
      } catch (e) {
        if (e?.status === 401 || e?.status === 403) {
          localStorage.removeItem("__extraconomy_token");
          localStorage.removeItem("__extraconomy_username");
          window.location.replace("/login");
        }
        showErrorToast({ detail: localeText("error") });
      } finally {
        setLoadingIntent(false);
      }
    },
    [token, username, recipient?.username, showErrorToast]
  );

  const amountDebounceRef = useRef(null);
  const handleAmountBlur = useCallback(() => {
    if (amountDebounceRef.current) {
      clearTimeout(amountDebounceRef.current);
      amountDebounceRef.current = null;
    }
    if (amount > 0 && recipient?.username) {
      fetchTransferIntent(amount);
    } else {
      setTransferIntentId(null);
      setFee(null);
    }
  }, [amount, recipient?.username, fetchTransferIntent]);

  useEffect(() => {
    if (amount <= 0 || !recipient?.username) {
      if (amount <= 0) {
        setTransferIntentId(null);
        setFee(null);
      }
      return;
    }
    if (amountDebounceRef.current) clearTimeout(amountDebounceRef.current);
    amountDebounceRef.current = setTimeout(() => {
      amountDebounceRef.current = null;
      fetchTransferIntent(amount);
    }, 400);
    return () => {
      if (amountDebounceRef.current) clearTimeout(amountDebounceRef.current);
    };
  }, [amount, recipient?.username, fetchTransferIntent]);

  const handleAuthError = useCallback((e) => {
    if (e?.status === 401 || e?.status === 403) {
      localStorage.removeItem("__extraconomy_token");
      localStorage.removeItem("__extraconomy_username");
      window.location.replace("/login");
    }
  }, []);

  const sendSecurityCode = useCallback(async () => {
    const res = await getUserInfoService.requestSecurityCode(token, username);
    const ok = res.messageCode === 200000 || res.messageCode === 200001;
    if (!ok) {
      const msg = localeText("data")?.find((m) => m.code === res.messageCode)?.message;
      showErrorToast({ detail: msg || localeText("error") });
    }
    return ok;
  }, [token, username, showErrorToast]);

  const handleOpenSecurityValidation = useCallback(async () => {
    if (!transferIntentId) return;
    setSendingCode(true);
    try {
      const ok = await sendSecurityCode();
      if (ok) setSecurityModalVisible(true);
    } catch (e) {
      handleAuthError(e);
      showErrorToast({ detail: localeText("error") });
    } finally {
      setSendingCode(false);
    }
  }, [transferIntentId, sendSecurityCode, handleAuthError, showErrorToast]);

  const handleResendCode = useCallback(async () => {
    setResendingCode(true);
    try {
      return await sendSecurityCode();
    } catch (e) {
      handleAuthError(e);
      showErrorToast({ detail: localeText("error") });
      return false;
    } finally {
      setResendingCode(false);
    }
  }, [sendSecurityCode, handleAuthError, showErrorToast]);

  const handleValidateCode = useCallback(
    async (code) => {
      if (!transferIntentId || !String(code).trim()) return;
      setValidatingCode(true);
      try {
        const res = await extracoinService.postFromAgree(token, username, transferIntentId, code);
        if (res.messageCode === 200000 || res.messageCode === 200001) {
          setSecurityModalVisible(false);
          setSuccess(true);
          showSuccessToast({ detail: localeText("transferCompleted") });
          refreshData?.();
        } else if (res.messageCode === 500166) {
          showErrorToast({ detail: localeText("securityCodeIsWrong") });
        } else {
          const msg = localeText("data")?.find((m) => m.code === res.messageCode)?.message;
          showErrorToast({ detail: msg || localeText("error") });
        }
      } catch (e) {
        handleAuthError(e);
        showErrorToast({ detail: localeText("error") });
      } finally {
        setValidatingCode(false);
      }
    },
    [token, username, transferIntentId, refreshData, showErrorToast, showSuccessToast, handleAuthError]
  );

  const canProceedStep2 =
    amount > 0 && amount <= usableBalance && transferIntentId != null && !loadingIntent;

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-content-center align-items-center min-h-screen">
          <Loading classNameIcon="text-8xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <nav className="flex align-items-center gap-2 mb-2 text-sm text-600" aria-label="Breadcrumb">
        <Link to="/dashboard-financeiro" className="text-600 hover:text-primary">
          {localeText("financial")}
        </Link>
        <span>/</span>
        <Link to="/dashboard-financeiro/extracredit" className="text-600 hover:text-primary">
          {localeText("extracredit")}
        </Link>
        <span>/</span>
        <span style={{ color: "var(--theme-text, #344563)" }}>{localeText("transfer")}</span>
      </nav>

      <div className="mb-4">
        <h2 className="text-lg font-semibold m-0" style={{ color: "var(--theme-text, #344563)" }}>
          {localeText("transferExtracredit") || "Transferir Extracredit"}
        </h2>
        <p className="text-sm text-600 m-0 mt-1">
          {localeText("transferExtracreditSubtitle") ||
            "Transfira Extracréditos para outros usuários no ecossistema Extraconomy."}
        </p>
      </div>

      <hr className="mb-6" style={{ borderColor: "var(--theme-border, #EBEBEB)" }} />

      <div className="p-6">
        {step === 1 && (
          <ExtracreditTransferStep1Recipient
            onRecipientFound={(r) => {
              setRecipient(r);
              if (r) setStep(2);
            }}
            onSearch={searchRecipient}
            loading={recipientSearchLoading}
            error={recipientSearchError}
            recipient={recipient}
          />
        )}
        {step === 2 && (
          <ExtracreditTransferStep2Values
            recipient={recipient}
            balance={balance}
            usableBalance={usableBalance}
            currencySymbol={currencySymbol}
            amount={amount}
            setAmount={setAmount}
            fee={fee}
            loadingIntent={loadingIntent}
            onAmountBlur={handleAmountBlur}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            canProceed={canProceedStep2}
          />
        )}
        {step === 3 && (
          <ExtracreditTransferStep3Summary
            recipient={recipient}
            amount={amount}
            fee={fee ?? 0}
            currencySymbol={currencySymbol}
            loading={sendingCode}
            success={success}
            onBack={() => setStep(2)}
            onConfirm={handleOpenSecurityValidation}
          />
        )}
      </div>

      <SecurityValidationModal
        visible={securityModalVisible}
        onHide={() => setSecurityModalVisible(false)}
        email={userInfo?.email || data?.userInfo?.email}
        amountLabel={`${formatPrice(amount)} ECR`}
        recipientName={recipient?.name || recipient?.firstName || recipient?.username || ""}
        validating={validatingCode}
        resending={resendingCode}
        onValidate={handleValidateCode}
        onResend={handleResendCode}
      />
    </DashboardLayout>
  );
};

export default ExtracreditTransferPage;
